import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';

import type {
  Holding,
  TopHoldings,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { DisclosureStatementButton } from '#/components/content/DisclosureStatement';
import { DataTable } from '#/components/data/DataTable';
import { NoData } from '#/components/ui/NoData';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { Ticker } from '#/components/ui/Ticker';
import type { ProductTypeSlug } from '#/types';
import {
  formatInteger,
  formatMonthDayYearDate,
  formatPercent,
} from '#/utils/format';

const DASH = '—';

// US tickers are alphabetic; a ticker containing a digit is a
// non-US identifier (e.g. an internationally-traded security Morningstar
// reports on but we don't have a detail page for) and shouldn't be linked.
const NUMERIC_TICKER_PATTERN = /\d/;

const HOLDING_TYPE_CATEGORY_SLUGS: Record<
  NonNullable<Holding['holding_type']>,
  ProductTypeSlug
> = {
  equity: 'equities',
  etf: 'etfs',
  mutual_fund: 'mutual-funds',
};

const columns: ColumnDef<Holding>[] = [
  {
    id: 'holding',
    accessorKey: 'name',
    header: 'Holding',
    enableSorting: false,
    size: 300,
    minSize: 150,
    maxSize: 300,
    cell: ({ row }) => {
      const wrapperClasses = 'flow flow-0 inline-flex items-center gap-8';
      const title = row.original.name ?? DASH;
      const category = row.original.holding_type
        ? HOLDING_TYPE_CATEGORY_SLUGS[row.original.holding_type]
        : null;

      const ticker = row.original.ticker;

      if (!ticker || !category || NUMERIC_TICKER_PATTERN.test(ticker)) {
        return (
          <span className={wrapperClasses}>
            <Ticker symbol={ticker ?? DASH} size="sm" />
            <span className="font-medium text-pretty">{title}</span>
          </span>
        );
      }

      return (
        <span className={wrapperClasses}>
          <Ticker symbol={ticker} size="sm" />
          <Link
            to="/$category/$id"
            params={{ category, id: ticker }}
            className="font-medium text-pretty"
          >
            {title}
          </Link>
        </span>
      );
    },
  },
  {
    accessorKey: 'sector',
    header: 'Sector',
    cell: ({ getValue }) => getValue<string | null>() ?? <NoData />,
  },
  {
    accessorKey: 'portfolio_weight_pct',
    header: 'Portfolio Weight',
    meta: { align: 'end' },
    cell: ({ getValue }) =>
      formatPercent(getValue<number | null>()) ?? <NoData />,
  },
];

interface TopHoldingsSectionProps {
  data: TopHoldings | null | undefined;
  description?: string | null;
}

export function TopHoldingsSection({
  data,
  description,
}: TopHoldingsSectionProps) {
  if (!data) {
    return null;
  }

  const holdings = data.top_holdings;
  const hasHoldings = (holdings?.length ?? 0) > 0;

  const stats = [
    {
      label: 'Portfolio Date',
      value: formatMonthDayYearDate(data.portfolio_date),
    },
    {
      label: 'Equity Holdings',
      value: formatInteger(data.num_equity_holdings),
    },
    {
      label: '% Assets in Top 10 Holdings',
      value: formatPercent(data.assets_in_top_10_holdings_pct),
    },
    {
      label: '% Reported Turnover',
      value: formatPercent(data.reported_turnover_pct),
    },
  ];
  const hasStats = stats.some((stat) => stat.value != null);

  if (!hasStats && !hasHoldings) {
    return null;
  }

  return (
    <section
      id="top-holdings"
      className="flow flow-inner-8 scroll-mt-(--scroll-offset)"
    >
      <SectionHeader
        title="Top Holdings"
        description={description}
        actions={<DisclosureStatementButton />}
      />

      {hasStats && (
        <div className="border-divider-subtle flow-24 border-t pt-24">
          <dl className="text-static-14 flex flex-wrap gap-y-12 xl:flex-nowrap xl:gap-x-12">
            {stats.map((stat) => (
              <div key={stat.label} className="w-1/2 shrink grow xl:w-full">
                <dt className="font-semibold xl:whitespace-nowrap">
                  {stat.label}
                </dt>
                <dd>{stat.value ?? <NoData />}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="flow-32 p-gap-inset border-divider-subtle -mx-gap border-t border-b sm:mx-0 sm:rounded sm:border">
        {hasHoldings && (
          <DataTable
            aria-label="Top holdings"
            columns={columns}
            data={holdings ?? []}
            initialSorting={[{ id: 'portfolio_weight_pct', desc: true }]}
          />
        )}
      </div>
    </section>
  );
}
